import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useWebSocket } from '../contexts/WebSocketContext';
import { usePermissions } from '../contexts/PermissionsContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Textarea } from '../components/ui/textarea';
import { FileUpload, FigmaLinkInput } from '../components/FileUpload';
import { MediaPreview } from '../components/MediaPreview';
import {
  Plus,
  Search,
  Filter,
  MoreVertical,
  FileImage,
  FileText,
  FileVideo,
  File,
  Upload,
  CheckCircle,
  XCircle,
  Clock,
  Loader2,
  Eye,
  Download,
  Trash2,
  Figma,
  Link,
  Send,
  Grid,
  List,
  Folder,
  FolderPlus,
  Tag,
  X,
  ChevronRight
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';

const ASSET_STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-slate-100 text-slate-700', icon: File },
  in_review: { label: 'In Review', color: 'bg-purple-50 text-purple-700', icon: Clock },
  approved: { label: 'Approved', color: 'bg-emerald-50 text-emerald-700', icon: CheckCircle },
  rejected: { label: 'Rejected', color: 'bg-red-50 text-red-700', icon: XCircle }
};

const FILE_TYPE_ICONS = {
  image: FileImage,
  video: FileVideo,
  document: FileText,
  design: Figma,
  link: Link,
  unknown: File
};

const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const AssetCard = ({ asset, users, onPreview, onEdit, onDelete, onSubmitForReview }) => {
  const owner = users.find(u => u.id === asset.owner_id);
  const statusConfig = ASSET_STATUS_CONFIG[asset.status];
  const FileIcon = FILE_TYPE_ICONS[asset.file_type] || FILE_TYPE_ICONS.unknown;
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  
  // Build thumbnail or preview URL
  const thumbnailUrl = asset.thumbnail_url 
    ? (asset.thumbnail_url.startsWith('/api') ? `${backendUrl}${asset.thumbnail_url}` : asset.thumbnail_url)
    : (asset.file_type === 'image' && asset.file_url?.startsWith('/api') 
        ? `${backendUrl}${asset.file_url}` 
        : null);

  return (
    <Card 
      className="mcc-card hover:shadow-md transition-all cursor-pointer group" 
      data-testid={`asset-card-${asset.id}`}
      onClick={() => onPreview(asset)}
    >
      <CardContent className="p-4">
        {/* Thumbnail/Preview Area */}
        <div className="aspect-video bg-slate-100 rounded-lg mb-3 overflow-hidden relative">
          {asset.file_type === 'image' && thumbnailUrl ? (
            <img 
              src={thumbnailUrl} 
              alt={asset.name}
              className="w-full h-full object-cover"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          ) : asset.file_type === 'video' ? (
            <div className="w-full h-full flex items-center justify-center bg-slate-900">
              <FileVideo className="w-12 h-12 text-white/50" />
            </div>
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <FileIcon className="w-12 h-12 text-slate-300" />
            </div>
          )}
          
          {/* Hover Overlay */}
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
            <Eye className="w-8 h-8 text-white" />
          </div>
        </div>

        <div className="flex items-start justify-between">
          <div className="min-w-0 flex-1">
            <h4 className="font-medium text-slate-900 truncate">{asset.name}</h4>
            <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
              <span>v{asset.version}</span>
              {asset.file_size && (
                <>
                  <span>•</span>
                  <span>{formatFileSize(asset.file_size)}</span>
                </>
              )}
            </div>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button variant="ghost" size="icon" className="h-7 w-7">
                <MoreVertical className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onPreview(asset); }}>
                <Eye className="w-4 h-4 mr-2" />
                Preview
              </DropdownMenuItem>
              {asset.file_url && (
                <DropdownMenuItem asChild>
                  <a 
                    href={asset.file_url.startsWith('/api') ? `${backendUrl}${asset.file_url}` : asset.file_url} 
                    download={asset.name}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download
                  </a>
                </DropdownMenuItem>
              )}
              {asset.status === 'draft' && (
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onSubmitForReview(asset); }}>
                  <Send className="w-4 h-4 mr-2" />
                  Submit for Review
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onEdit(asset); }}>
                Edit Details
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={(e) => { e.stopPropagation(); onDelete(asset.id); }} 
                className="text-red-600"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        
        <div className="flex items-center gap-2 mt-3">
          <Badge className={statusConfig?.color}>{statusConfig?.label}</Badge>
          <span className="text-xs text-slate-400">•</span>
          <span className="text-xs text-slate-500 truncate">{owner?.name || 'Unknown'}</span>
        </div>
      </CardContent>
    </Card>
  );
};

const AssetListItem = ({ asset, users, onPreview, onEdit, onDelete, onSubmitForReview }) => {
  const owner = users.find(u => u.id === asset.owner_id);
  const statusConfig = ASSET_STATUS_CONFIG[asset.status];
  const FileIcon = FILE_TYPE_ICONS[asset.file_type] || FILE_TYPE_ICONS.unknown;
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  return (
    <div 
      className="flex items-center gap-4 p-3 border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors"
      onClick={() => onPreview(asset)}
      data-testid={`asset-list-item-${asset.id}`}
    >
      <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
        <FileIcon className="w-6 h-6 text-slate-400" />
      </div>
      
      <div className="flex-1 min-w-0">
        <h4 className="font-medium text-slate-900 truncate">{asset.name}</h4>
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <span>v{asset.version}</span>
          {asset.file_size && (
            <>
              <span>•</span>
              <span>{formatFileSize(asset.file_size)}</span>
            </>
          )}
          <span>•</span>
          <span>{owner?.name || 'Unknown'}</span>
        </div>
      </div>
      
      <Badge className={`${statusConfig?.color} flex-shrink-0`}>{statusConfig?.label}</Badge>
      
      <span className="text-xs text-slate-400 flex-shrink-0">
        {asset.updated_at ? format(parseISO(asset.updated_at), 'MMM d, yyyy') : '-'}
      </span>
      
      <DropdownMenu>
        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
          <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0">
            <MoreVertical className="w-4 h-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onPreview(asset); }}>
            <Eye className="w-4 h-4 mr-2" />
            Preview
          </DropdownMenuItem>
          {asset.file_url && (
            <DropdownMenuItem asChild>
              <a 
                href={asset.file_url.startsWith('/api') ? `${backendUrl}${asset.file_url}` : asset.file_url} 
                download={asset.name}
                onClick={(e) => e.stopPropagation()}
              >
                <Download className="w-4 h-4 mr-2" />
                Download
              </a>
            </DropdownMenuItem>
          )}
          {asset.status === 'draft' && (
            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onSubmitForReview(asset); }}>
              <Send className="w-4 h-4 mr-2" />
              Submit for Review
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onEdit(asset); }}>
            Edit Details
          </DropdownMenuItem>
          <DropdownMenuItem 
            onClick={(e) => { e.stopPropagation(); onDelete(asset.id); }} 
            className="text-red-600"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export const AssetsPage = () => {
  const { api, user } = useAuth();
  const { subscribe } = useWebSocket();
  const { can } = usePermissions();
  const [assets, setAssets] = useState([]);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [content, setContent] = useState([]);
  const [searchParams] = useSearchParams();
  const urlTab = searchParams.get('tab');
  
  // Folder state
  const [folders, setFolders] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [folderPath, setFolderPath] = useState([]);
  const [folderDialogOpen, setFolderDialogOpen] = useState(false);
  const [editingFolder, setEditingFolder] = useState(null);
  const [folderFormData, setFolderFormData] = useState({ name: '', description: '', color: '#3b82f6' });
  const [moveFolderDialogOpen, setMoveFolderDialogOpen] = useState(false);
  const [movingFolder, setMovingFolder] = useState(null);
  const [moveTargetFolder, setMoveTargetFolder] = useState('root');
  
  // Tagging state
  const [allTags, setAllTags] = useState([]);
  const [newTag, setNewTag] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [businessUnitFilter, setBusinessUnitFilter] = useState('all');
  const [businessUnits, setBusinessUnits] = useState([]);
  const [requestTypes, setRequestTypes] = useState([]);
  
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState(urlTab === 'pending' ? 'review' : 'assets');
  
  // Preview state
  const [previewAsset, setPreviewAsset] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  
  // Upload state
  const [uploadedFile, setUploadedFile] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    file_url: '',
    file_type: 'unknown',
    file_size: null,
    mime_type: null,
    figma_url: '',
    version: 1,
    content_id: '',
    project_id: '',
    owner_id: '',
    status: 'draft',
    folder_id: '',
    business_unit: '',
    request_type: '',
    tags: []
  });

  const fetchData = useCallback(async () => {
    try {
      const [assetsRes, usersRes, projectsRes, contentRes, foldersRes, tagsRes, configRes] = await Promise.all([
        api.get('/assets'),
        api.get('/users'),
        api.get('/projects'),
        api.get('/content'),
        api.get('/assets/folders'),
        api.get('/assets/tags/all'),
        api.get('/assets/config')
      ]);
      setAssets(assetsRes.data);
      setUsers(usersRes.data);
      setProjects(projectsRes.data);
      setContent(contentRes.data);
      setFolders(foldersRes.data);
      setAllTags(tagsRes.data || []);
      setBusinessUnits(configRes.data.business_units || []);
      setRequestTypes(configRes.data.request_types || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Real-time updates
  useEffect(() => {
    const unsubscribes = [];
    
    unsubscribes.push(subscribe('asset_created', (message) => {
      if (message.data) {
        setAssets(prev => {
          if (prev.some(a => a.id === message.data.id)) return prev;
          return [...prev, message.data];
        });
      }
    }));
    
    unsubscribes.push(subscribe('asset_updated', (message) => {
      if (message.data) {
        setAssets(prev => prev.map(a => 
          a.id === message.data.id ? { ...a, ...message.data } : a
        ));
        // Update preview if viewing the same asset
        if (previewAsset?.id === message.data.id) {
          setPreviewAsset(prev => ({ ...prev, ...message.data }));
        }
      }
    }));
    
    unsubscribes.push(subscribe('asset_deleted', (message) => {
      if (message.data?.id) {
        setAssets(prev => prev.filter(a => a.id !== message.data.id));
        // Close preview if deleted asset was being viewed
        if (previewAsset?.id === message.data.id) {
          setPreviewOpen(false);
          setPreviewAsset(null);
        }
      }
    }));

    return () => unsubscribes.forEach(unsub => unsub());
  }, [subscribe, previewAsset?.id]);

  // Folder CRUD functions
  const handleCreateFolder = async () => {
    try {
      const payload = {
        ...folderFormData,
        parent_id: currentFolder || null
      };
      await api.post('/assets/folders', payload);
      setFolderDialogOpen(false);
      setFolderFormData({ name: '', description: '', color: '#3b82f6' });
      fetchData();
    } catch (error) {
      console.error('Failed to create folder:', error);
    }
  };

  const handleDeleteFolder = async (folderId) => {
    if (!window.confirm('Delete this folder? Assets will be moved to root.')) return;
    try {
      await api.delete(`/assets/folders/${folderId}`);
      fetchData();
    } catch (error) {
      console.error('Failed to delete folder:', error);
    }
  };

  const navigateToFolder = (folder) => {
    if (folder) {
      setCurrentFolder(folder.id);
      setFolderPath(prev => [...prev, folder]);
    } else {
      setCurrentFolder(null);
      setFolderPath([]);
    }
  };

  const navigateUp = (index) => {
    if (index < 0) {
      setCurrentFolder(null);
      setFolderPath([]);
    } else {
      const folder = folderPath[index];
      setCurrentFolder(folder.id);
      setFolderPath(folderPath.slice(0, index + 1));
    }
  };

  const handleMoveFolder = async () => {
    if (!movingFolder) return;
    try {
      const newParentId = moveTargetFolder === 'root' ? null : moveTargetFolder;
      await api.put(`/assets/folders/${movingFolder.id}`, { parent_id: newParentId });
      setMoveFolderDialogOpen(false);
      setMovingFolder(null);
      setMoveTargetFolder('root');
      fetchData();
      toast.success(`Folder "${movingFolder.name}" moved successfully`);
    } catch (error) {
      console.error('Failed to move folder:', error);
      toast.error(error.response?.data?.detail || 'Failed to move folder');
    }
  };

  // Tag management
  const addTagToAsset = async (assetId, tag) => {
    if (!tag.trim()) return;
    try {
      await api.post(`/assets/${assetId}/tags?tag=${encodeURIComponent(tag.trim())}`);
      fetchData();
    } catch (error) {
      console.error('Failed to add tag:', error);
    }
  };

  const removeTagFromAsset = async (assetId, tag) => {
    try {
      await api.delete(`/assets/${assetId}/tags/${encodeURIComponent(tag)}`);
      fetchData();
    } catch (error) {
      console.error('Failed to remove tag:', error);
    }
  };

  const handleUploadComplete = (result) => {
    setUploadedFile(result);
    setFormData(prev => ({
      ...prev,
      name: prev.name || result.file_name.split('.')[0],
      file_url: result.file_url,
      file_type: result.file_type,
      file_size: result.file_size,
      mime_type: result.mime_type
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...formData,
        owner_id: formData.owner_id || user.id,
        content_id: formData.content_id || null,
        project_id: formData.project_id || null,
        folder_id: formData.folder_id || null,
        version: parseInt(formData.version),
        file_size: formData.file_size || null,
        figma_url: formData.figma_url || null,
        tags: formData.tags || []
      };
      
      // If it's a Figma link, set file_type to design
      if (payload.figma_url && !payload.file_url) {
        payload.file_type = 'design';
        payload.file_url = payload.figma_url;
      }
      
      if (editingAsset) {
        await api.put(`/assets/${editingAsset.id}`, payload);
      } else {
        await api.post('/assets', payload);
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Failed to save asset:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (asset) => {
    setEditingAsset(asset);
    setFormData({
      name: asset.name,
      file_url: asset.file_url,
      file_type: asset.file_type || 'unknown',
      file_size: asset.file_size,
      mime_type: asset.mime_type,
      figma_url: asset.figma_url || '',
      version: asset.version,
      content_id: asset.content_id || '',
      project_id: asset.project_id || '',
      owner_id: asset.owner_id,
      status: asset.status,
      folder_id: asset.folder_id || '',
      business_unit: asset.business_unit || '',
      request_type: asset.request_type || '',
      tags: asset.tags || []
    });
    setDialogOpen(true);
  };

  const handleDelete = async (assetId) => {
    if (!window.confirm('Are you sure you want to delete this asset?')) return;
    try {
      await api.delete(`/assets/${assetId}`);
      fetchData();
    } catch (error) {
      console.error('Failed to delete asset:', error);
    }
  };

  const handleSubmitForReview = async (asset) => {
    try {
      await api.put(`/assets/${asset.id}`, { status: 'in_review' });
      fetchData();
    } catch (error) {
      console.error('Failed to submit for review:', error);
    }
  };

  const handleApprove = async (assetId) => {
    setIsApproving(true);
    try {
      await api.put(`/assets/${assetId}`, { status: 'approved' });
      fetchData();
    } catch (error) {
      console.error('Failed to approve:', error);
    } finally {
      setIsApproving(false);
    }
  };

  const handleReject = async (assetId) => {
    setIsApproving(true);
    try {
      await api.put(`/assets/${assetId}`, { status: 'rejected' });
      fetchData();
    } catch (error) {
      console.error('Failed to reject:', error);
    } finally {
      setIsApproving(false);
    }
  };

  const resetForm = () => {
    setEditingAsset(null);
    setUploadedFile(null);
    setFormData({
      name: '',
      file_url: '',
      file_type: 'unknown',
      file_size: null,
      mime_type: null,
      figma_url: '',
      version: 1,
      content_id: '',
      project_id: '',
      owner_id: '',
      status: 'draft',
      folder_id: currentFolder || '',
      business_unit: '',
      request_type: '',
      tags: []
    });
  };

  const handlePreview = (asset) => {
    setPreviewAsset(asset);
    setPreviewOpen(true);
  };

  // Filters
  const filteredAssets = assets.filter(a => {
    const matchesSearch = a.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (a.tags || []).some(t => t.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesStatus = statusFilter === 'all' || a.status === statusFilter;
    const matchesType = typeFilter === 'all' || a.file_type === typeFilter;
    const matchesFolder = !currentFolder ? true : a.folder_id === currentFolder;
    const matchesBusinessUnit = businessUnitFilter === 'all' || a.business_unit === businessUnitFilter;
    const matchesTag = !tagFilter || (a.tags || []).includes(tagFilter);
    return matchesSearch && matchesStatus && matchesType && matchesFolder && matchesBusinessUnit && matchesTag;
  });

  // Get folders in current directory
  const currentFolders = folders.filter(f => 
    currentFolder ? f.parent_id === currentFolder : !f.parent_id
  );

  // Assets awaiting approval (for admins/managers)
  const pendingReviewAssets = assets.filter(a => a.status === 'in_review');
  
  // Check if user can approve
  const canApprove = can('asset:approve') || user?.role === 'admin' || user?.role === 'manager';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="assets-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Assets & Approvals</h1>
          <p className="text-slate-500 mt-1">
            {assets.length} assets • {pendingReviewAssets.length} pending review
          </p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="gap-2 bg-blue-600 hover:bg-blue-700" data-testid="new-asset-btn">
              <Upload className="w-4 h-4" />
              Upload Asset
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingAsset ? 'Edit Asset' : 'Upload New Asset'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* File Upload Section */}
              {!editingAsset && (
                <div className="space-y-4">
                  <Tabs defaultValue="upload" className="w-full">
                    <TabsList className="w-full">
                      <TabsTrigger value="upload" className="flex-1">Upload File</TabsTrigger>
                      <TabsTrigger value="figma" className="flex-1">Figma Link</TabsTrigger>
                      <TabsTrigger value="url" className="flex-1">External URL</TabsTrigger>
                    </TabsList>
                    
                    <TabsContent value="upload" className="mt-4">
                      <FileUpload 
                        api={api} 
                        onUploadComplete={handleUploadComplete}
                        onError={(error) => console.error('Upload error:', error)}
                      />
                    </TabsContent>
                    
                    <TabsContent value="figma" className="mt-4">
                      <FigmaLinkInput 
                        value={formData.figma_url}
                        onChange={(url) => setFormData({ ...formData, figma_url: url, file_type: 'design' })}
                      />
                    </TabsContent>
                    
                    <TabsContent value="url" className="mt-4">
                      <div className="space-y-2">
                        <Label>External URL</Label>
                        <Input
                          type="url"
                          placeholder="https://example.com/asset.png"
                          value={formData.file_url}
                          onChange={(e) => setFormData({ ...formData, file_url: e.target.value, file_type: 'link' })}
                          data-testid="external-url-input"
                        />
                        <p className="text-xs text-slate-500">
                          Link to an externally hosted file
                        </p>
                      </div>
                    </TabsContent>
                  </Tabs>
                </div>
              )}
              
              {/* Asset Details */}
              <div className="space-y-4 pt-4 border-t">
                <div className="space-y-2">
                  <Label>Asset Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="Hero Banner v2"
                    required
                    data-testid="asset-name-input"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Version</Label>
                    <Input
                      type="number"
                      value={formData.version}
                      onChange={(e) => setFormData({...formData, version: e.target.value})}
                      min="1"
                      data-testid="asset-version-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Status</Label>
                    <Select value={formData.status} onValueChange={(v) => setFormData({...formData, status: v})}>
                      <SelectTrigger data-testid="asset-status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(ASSET_STATUS_CONFIG).map(([key, config]) => (
                          <SelectItem key={key} value={key}>{config.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Project</Label>
                    <Select value={formData.project_id || "none"} onValueChange={(v) => setFormData({...formData, project_id: v === "none" ? "" : v})}>
                      <SelectTrigger data-testid="asset-project-select">
                        <SelectValue placeholder="Select project" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {projects.map(p => (
                          <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Content Item</Label>
                    <Select value={formData.content_id || "none"} onValueChange={(v) => setFormData({...formData, content_id: v === "none" ? "" : v})}>
                      <SelectTrigger data-testid="asset-content-select">
                        <SelectValue placeholder="Select content" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {content.map(c => (
                          <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* NEW: Folder, Business Unit, Request Type */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Folder</Label>
                  <Select value={formData.folder_id || "none"} onValueChange={(v) => setFormData({...formData, folder_id: v === "none" ? "" : v})}>
                    <SelectTrigger data-testid="asset-folder-select">
                      <SelectValue placeholder="Select folder" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Root (No Folder)</SelectItem>
                      {folders.map(f => (
                        <SelectItem key={f.id} value={f.id}>
                          <span className="flex items-center gap-2">
                            <Folder className="w-4 h-4" style={{ color: f.color }} />
                            {f.name}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Business Unit</Label>
                  <Select value={formData.business_unit || "none"} onValueChange={(v) => setFormData({...formData, business_unit: v === "none" ? "" : v})}>
                    <SelectTrigger data-testid="asset-business-unit-select">
                      <SelectValue placeholder="Select business unit" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {businessUnits.map(unit => (
                        <SelectItem key={unit.value} value={unit.value}>{unit.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Tags */}
              <div className="space-y-2">
                <Label>Tags</Label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {(formData.tags || []).map(tag => (
                    <Badge key={tag} variant="secondary" className="gap-1">
                      {tag}
                      <X 
                        className="w-3 h-3 cursor-pointer" 
                        onClick={() => setFormData({...formData, tags: formData.tags.filter(t => t !== tag)})}
                      />
                    </Badge>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    placeholder="Add a tag..."
                    className="flex-1"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && newTag.trim()) {
                        e.preventDefault();
                        if (!formData.tags.includes(newTag.trim())) {
                          setFormData({...formData, tags: [...(formData.tags || []), newTag.trim()]});
                        }
                        setNewTag('');
                      }
                    }}
                  />
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
                        setFormData({...formData, tags: [...(formData.tags || []), newTag.trim()]});
                        setNewTag('');
                      }
                    }}
                  >
                    <Tag className="w-4 h-4" />
                  </Button>
                </div>
                {allTags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    <span className="text-xs text-slate-500">Suggestions:</span>
                    {allTags.slice(0, 8).map(tag => (
                      <Badge 
                        key={tag} 
                        variant="outline" 
                        className="cursor-pointer text-xs"
                        onClick={() => {
                          if (!formData.tags.includes(tag)) {
                            setFormData({...formData, tags: [...(formData.tags || []), tag]});
                          }
                        }}
                      >
                        {tag}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={saving || (!formData.file_url && !formData.figma_url && !uploadedFile)} 
                  data-testid="save-asset-btn"
                >
                  {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  {editingAsset ? 'Update' : 'Create'} Asset
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="assets" data-testid="tab-assets">
            All Assets ({assets.length})
          </TabsTrigger>
          {canApprove && (
            <TabsTrigger value="review" data-testid="tab-review">
              Pending Review ({pendingReviewAssets.length})
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="assets" className="space-y-4">
          {/* Folder Navigation */}
          <Card className="mcc-card">
            <CardContent className="p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {/* Breadcrumb */}
                  <Button variant="ghost" size="sm" onClick={() => navigateUp(-1)} className="gap-1">
                    <Folder className="w-4 h-4" />
                    Root
                  </Button>
                  {folderPath.map((folder, index) => (
                    <span key={folder.id} className="flex items-center">
                      <ChevronRight className="w-4 h-4 text-slate-400" />
                      <Button variant="ghost" size="sm" onClick={() => navigateUp(index)}>
                        {folder.name}
                      </Button>
                    </span>
                  ))}
                </div>
                <Button variant="outline" size="sm" onClick={() => setFolderDialogOpen(true)} className="gap-1">
                  <FolderPlus className="w-4 h-4" />
                  New Folder
                </Button>
              </div>
              
              {/* Subfolders */}
              {currentFolders.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t">
                  {currentFolders.map(folder => (
                    <div
                      key={folder.id}
                      className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors group"
                      onClick={() => navigateToFolder(folder)}
                    >
                      <Folder className="w-5 h-5" style={{ color: folder.color }} />
                      <span className="font-medium text-sm">{folder.name}</span>
                      <span className="text-xs text-slate-400">({folder.asset_count})</span>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="icon" className="w-6 h-6 opacity-0 group-hover:opacity-100">
                            <MoreVertical className="w-3 h-3" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setMovingFolder(folder); setMoveFolderDialogOpen(true); }}>
                            <Folder className="w-4 h-4 mr-2" />
                            Move Folder
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            onClick={(e) => { e.stopPropagation(); handleDeleteFolder(folder.id); }}
                            className="text-red-600"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete Folder
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Create Folder Dialog */}
          <Dialog open={folderDialogOpen} onOpenChange={setFolderDialogOpen}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Create New Folder</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Folder Name</Label>
                  <Input
                    value={folderFormData.name}
                    onChange={(e) => setFolderFormData({...folderFormData, name: e.target.value})}
                    placeholder="My Folder"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={folderFormData.description}
                    onChange={(e) => setFolderFormData({...folderFormData, description: e.target.value})}
                    placeholder="Optional description"
                    rows={2}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Color</Label>
                  <div className="flex gap-2">
                    {['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#6b7280'].map(color => (
                      <button
                        key={color}
                        type="button"
                        className={`w-8 h-8 rounded-full ${folderFormData.color === color ? 'ring-2 ring-offset-2 ring-blue-500' : ''}`}
                        style={{ backgroundColor: color }}
                        onClick={() => setFolderFormData({...folderFormData, color})}
                      />
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => setFolderDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleCreateFolder} disabled={!folderFormData.name}>Create Folder</Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Move Folder Dialog */}
          <Dialog open={moveFolderDialogOpen} onOpenChange={setMoveFolderDialogOpen}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Move Folder: {movingFolder?.name}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Move to</Label>
                  <Select value={moveTargetFolder} onValueChange={setMoveTargetFolder}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select destination" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="root">Root (Top Level)</SelectItem>
                      {folders
                        .filter(f => f.id !== movingFolder?.id)
                        .map(f => (
                          <SelectItem key={f.id} value={f.id}>
                            <span className="flex items-center gap-2">
                              <Folder className="w-4 h-4" style={{ color: f.color }} />
                              {f.name}
                            </span>
                          </SelectItem>
                        ))
                      }
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => setMoveFolderDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleMoveFolder}>Move Folder</Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px] max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search assets or tags..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="search-assets"
              />
            </div>
            
            <Select value={businessUnitFilter} onValueChange={setBusinessUnitFilter}>
              <SelectTrigger className="w-[160px]" data-testid="filter-business-unit">
                <SelectValue placeholder="Business Unit" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Business Units</SelectItem>
                {businessUnits.map(unit => (
                  <SelectItem key={unit.value} value={unit.value}>{unit.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[140px]" data-testid="filter-status">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {Object.entries(ASSET_STATUS_CONFIG).map(([key, config]) => (
                  <SelectItem key={key} value={key}>{config.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[140px]" data-testid="filter-type">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="image">Images</SelectItem>
                <SelectItem value="video">Videos</SelectItem>
                <SelectItem value="document">Documents</SelectItem>
                <SelectItem value="design">Designs</SelectItem>
              </SelectContent>
            </Select>
            
            {allTags.length > 0 && (
              <Select value={tagFilter} onValueChange={setTagFilter}>
                <SelectTrigger className="w-[140px]" data-testid="filter-tag">
                  <Tag className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Tag" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">All Tags</SelectItem>
                  {allTags.map(tag => (
                    <SelectItem key={tag} value={tag}>{tag}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            
            <div className="flex items-center border rounded-md ml-auto">
              <Button 
                variant={viewMode === 'grid' ? 'secondary' : 'ghost'} 
                size="sm"
                onClick={() => setViewMode('grid')}
                className="rounded-r-none"
              >
                <Grid className="w-4 h-4" />
              </Button>
              <Button 
                variant={viewMode === 'list' ? 'secondary' : 'ghost'} 
                size="sm"
                onClick={() => setViewMode('list')}
                className="rounded-l-none"
              >
                <List className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Assets View */}
          {filteredAssets.length === 0 ? (
            <Card className="mcc-card">
              <CardContent className="py-12 text-center">
                <FileImage className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 mb-2">No assets found</h3>
                <p className="text-slate-500">Upload your first asset to get started</p>
              </CardContent>
            </Card>
          ) : viewMode === 'grid' ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filteredAssets.map(asset => (
                <AssetCard
                  key={asset.id}
                  asset={asset}
                  users={users}
                  onPreview={handlePreview}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onSubmitForReview={handleSubmitForReview}
                />
              ))}
            </div>
          ) : (
            <Card className="mcc-card">
              <CardContent className="p-0">
                {filteredAssets.map(asset => (
                  <AssetListItem
                    key={asset.id}
                    asset={asset}
                    users={users}
                    onPreview={handlePreview}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onSubmitForReview={handleSubmitForReview}
                  />
                ))}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {canApprove && (
          <TabsContent value="review" className="space-y-4">
            {pendingReviewAssets.length === 0 ? (
              <Card className="mcc-card">
                <CardContent className="py-12 text-center">
                  <CheckCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">No pending reviews</h3>
                  <p className="text-slate-500">All assets have been reviewed</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {pendingReviewAssets.map(asset => (
                  <AssetCard
                    key={asset.id}
                    asset={asset}
                    users={users}
                    onPreview={handlePreview}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onSubmitForReview={handleSubmitForReview}
                  />
                ))}
              </div>
            )}
          </TabsContent>
        )}
      </Tabs>

      {/* Media Preview Modal */}
      <MediaPreview
        asset={previewAsset}
        open={previewOpen}
        onClose={setPreviewOpen}
        onApprove={handleApprove}
        onReject={handleReject}
        canApprove={canApprove}
        isApproving={isApproving}
      />
    </div>
  );
};
